import React from "react";
import Icon from "../OldFront/Simple/IconComponent/Icon";
import { ImageGalleryLightbox } from "./ImageGalleryLightbox";
import { ImageGalleryThumbnails } from "./ImageGalleryThumbnails";
import { IImages, preloadImagesAround, wrapImageIndex } from "./imageGalleryImages";

const AXIS_LOCK_PX = 10;
const SWIPE_MINIMUM_FRACTION_OF_STAGE = 0.15;
const SWIPE_MINIMUM_PX = 50;

interface IImageGallery {
    images: Array<IImages>;
    showCarousel: boolean;
}

interface IImageGalleryState {
    currentImageIndex: number;
    lightboxIsOpen: boolean;
    dragOffsetPx: number;
    isDragging: boolean;
}

export class ImageGallery extends React.Component<React.PropsWithChildren<IImageGallery>, IImageGalleryState> {
    private stageRef = React.createRef<HTMLDivElement>();
    private touchOrigin: { x: number, y: number } | undefined;
    private touchAxis: "undecided" | "horizontal" | "vertical" = "undecided";
    private lastGestureWasASwipe = false;

    constructor(props: IImageGallery) {
        super(props);
        this.state = {
            currentImageIndex: 0,
            lightboxIsOpen: false,
            dragOffsetPx: 0,
            isDragging: false,
        };
    }

    public componentDidMount(): void {
        preloadImagesAround(this.props.images, this.state.currentImageIndex);
    }

    public componentDidUpdate(previousProps: IImageGallery, previousState: IImageGalleryState): void {
        if (previousProps.images.length !== this.props.images.length) {
            this.setState((state) => ({
                currentImageIndex: wrapImageIndex(state.currentImageIndex, this.props.images.length),
            }));
        }

        if (previousState.currentImageIndex !== this.state.currentImageIndex) {
            preloadImagesAround(this.props.images, this.state.currentImageIndex);
        }
    }

    public render (): JSX.Element {
        const imagesCount = this.props.images.length;

        if (imagesCount === 0) {
            return <div className="imageGallery" />;
        }

        const currentImageIndex = this.state.currentImageIndex;
        const hasMultipleImages = imagesCount > 1;

        return (
            <div className="imageGallery">
                <div
                    className="imageGallery__stage"
                    ref={this.stageRef}
                    onKeyDown={this.onKeyDown}
                    onTouchStart={this.onTouchStart}
                    onTouchMove={this.onTouchMove}
                    onTouchEnd={this.onTouchEnd}
                    onTouchCancel={this.onTouchCancel}
                >
                    {/* The transform is the one thing here that cannot live in the
                        stylesheet — it tracks a finger, in pixels, between slides. */}
                    <div
                        className={
                            "imageGallery__reel" +
                            (this.state.isDragging ? " imageGallery__reel--dragging" : "")
                        }
                        style={{
                            transform: `translateX(calc(${-currentImageIndex * 100}% + ${this.state.dragOffsetPx}px))`,
                        }}
                    >
                        {this.props.images.map((image, index) => (
                            <div className="imageGallery__slide" key={index}>
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    draggable={false}
                                    decoding="async"
                                    loading={index === 0 ? "eager" : "lazy"}
                                />
                            </div>
                        ))}
                    </div>

                    {/* An overlay rather than wrapping the reel: the previous/next
                        controls are buttons and cannot be nested inside another one. */}
                    <button
                        type="button"
                        className="imageGallery__expand-target"
                        onClick={this.openLightbox}
                        aria-label={`Expand photo ${currentImageIndex + 1} of ${imagesCount}`}
                    />

                    <div className="imageGallery__counter" aria-hidden={true}>
                        <Icon iconName="camera" height={2} width={2} type="mouse" />
                        <span className="imageGallery__counter-text">
                            {`${currentImageIndex + 1} / ${imagesCount}`}
                        </span>
                    </div>

                    <div className="imageGallery__expand-hint" aria-hidden={true}>
                        <Icon iconName="expand" height={1.8} width={1.8} type="mouse" />
                    </div>

                    {hasMultipleImages &&
                        <button
                            type="button"
                            className="imageGallery__nav imageGallery__nav--previous"
                            onClick={this.goToPreviousImage}
                            aria-label="Previous photo"
                        >
                            <div className="imageGallery__nav-button">
                                <Icon iconName="leftArrow" height={2.4} width={2.4} type="mouse" />
                            </div>
                        </button>
                    }
                    {hasMultipleImages &&
                        <button
                            type="button"
                            className="imageGallery__nav imageGallery__nav--next"
                            onClick={this.goToNextImage}
                            aria-label="Next photo"
                        >
                            <div className="imageGallery__nav-button">
                                <Icon iconName="rightArrow" height={2.4} width={2.4} type="mouse" />
                            </div>
                        </button>
                    }
                </div>

                {this.props.showCarousel && hasMultipleImages &&
                    <ImageGalleryThumbnails
                        images={this.props.images}
                        currentImageIndex={currentImageIndex}
                        size="inline"
                        onSelectImage={this.selectImage}
                    />
                }

                {this.state.lightboxIsOpen &&
                    <ImageGalleryLightbox
                        images={this.props.images}
                        currentImageIndex={currentImageIndex}
                        onSelectImage={this.selectImage}
                        onClose={this.closeLightbox}
                    />
                }
            </div>
        );
    }

    private selectImage = (index: number): void =>
        this.setState({ currentImageIndex: wrapImageIndex(index, this.props.images.length) });

    private goToPreviousImage = (): void => this.selectImage(this.state.currentImageIndex - 1);

    private goToNextImage = (): void => this.selectImage(this.state.currentImageIndex + 1);

    private openLightbox = (): void => {
        if (this.lastGestureWasASwipe) {
            this.lastGestureWasASwipe = false;
            return;
        }

        this.setState({ lightboxIsOpen: true });
    };

    private closeLightbox = (): void => this.setState({ lightboxIsOpen: false });

    private onKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            this.goToPreviousImage();
            return;
        }

        if (e.key === "ArrowRight") {
            e.preventDefault();
            this.goToNextImage();
        }
    };

    private onTouchStart = (e: React.TouchEvent): void => {
        const touch = e.touches[0];
        this.touchOrigin = { x: touch.clientX, y: touch.clientY };
        this.touchAxis = "undecided";
        this.lastGestureWasASwipe = false;
    };

    private onTouchMove = (e: React.TouchEvent): void => {
        if (this.touchOrigin === undefined) {
            return;
        }

        const touch = e.touches[0];
        const dx = touch.clientX - this.touchOrigin.x;
        const dy = touch.clientY - this.touchOrigin.y;

        if (this.touchAxis === "undecided") {
            if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) {
                return;
            }

            this.touchAxis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
        }

        if (this.touchAxis !== "horizontal") {
            return;
        }

        this.lastGestureWasASwipe = true;
        this.setState({ isDragging: true, dragOffsetPx: dx });
    };

    private onTouchEnd = (e: React.TouchEvent): void => {
        const origin = this.touchOrigin;
        const axis = this.touchAxis;
        this.onTouchCancel();

        if (origin === undefined || axis !== "horizontal") {
            return;
        }

        const dx = e.changedTouches[0].clientX - origin.x;
        const stageWidth = this.stageRef.current === null ? 0 : this.stageRef.current.clientWidth;
        const threshold = Math.max(SWIPE_MINIMUM_PX, stageWidth * SWIPE_MINIMUM_FRACTION_OF_STAGE);

        if (dx <= -threshold) {
            this.goToNextImage();
            return;
        }

        if (dx >= threshold) {
            this.goToPreviousImage();
        }
    };

    private onTouchCancel = (): void => {
        this.touchOrigin = undefined;
        this.touchAxis = "undecided";

        if (this.state.isDragging || this.state.dragOffsetPx !== 0) {
            this.setState({ isDragging: false, dragOffsetPx: 0 });
        }
    };
}
